import { useEffect, useState } from 'react';

/**
 * TANDA 27b — «¿CABE?», PREGUNTADO EN JAVASCRIPT Y NO EN CSS.
 *
 * ── POR QUÉ ESTO EXISTE, HABIENDO CLASES DE TAILWIND ────────────────────────
 *
 * Porque CSS no puede contestar la pregunta que la tarjeta del Panel necesita.
 *
 * `hidden md:block` decide qué se VE, y para eso es lo correcto: sin listeners,
 * sin re-render, y se actualiza sola al girar el teléfono. Cuando las dos
 * versiones de algo viven en el MISMO bloque —el QR y el badge de la landing—
 * eso es todo lo que hace falta, y ahí se sigue usando.
 *
 * Las dos variantes de la tarjeta del Panel no viven en el mismo bloque: la
 * lateral está en `components/Sidebar.js` (el chasis) y la de lista en
 * `pages/Dashboard.js` (la pantalla), en ramas del árbol que no se tocan. Con
 * CSS las DOS estarían montadas y una sólo estaría `display:none` — dos
 * tarjetas en el DOM, dos títulos iguales, dos botones de cerrar con el mismo
 * `aria-label`, y un lector de pantalla leyendo la que nadie ve. Aquí hace falta
 * que una de las dos NO EXISTA.
 *
 * ── POR QUÉ `matchMedia` Y NO `window.innerWidth` ───────────────────────────
 *
 * `innerWidth` leído en el render se queda viejo: al girar el teléfono o
 * arrastrar la ventana nadie vuelve a preguntar, y la objeción está escrita en
 * el propio `AppStoreBloque.js` desde la TANDA 27. `matchMedia` no tiene ese
 * problema — trae su evento `change`, así que el componente se entera del cambio
 * de ancho igual que se enteraría una regla de CSS.
 *
 * ── EL DEFAULT, Y POR QUÉ ESTÁ ESCRITO EN VEZ DE HEREDADO ───────────────────
 *
 * En jsdom `window.matchMedia` es **undefined**: este repo no tiene
 * `setupTests.js` ni `setupFiles` de jest, y por eso `AdaptiveStudy.js:268` y
 * `LandingPage.js:24` ya lo llaman con `?.`. Sin un default explícito, «no hay
 * matchMedia» se colaría como `false` y la respuesta sería «angosto» — o sea que
 * un entorno sin la API decidiría el layout por accidente.
 *
 * Se devuelve `porDefecto` (que quien llama elige) y las suites instalan su
 * doble. En un navegador de verdad esto no ocurre nunca: `matchMedia` existe
 * desde IE10.
 *
 * @param {string} consulta  una media query, p. ej. `(min-width: 768px)`
 * @param {boolean} porDefecto  qué contestar si no hay `matchMedia`
 */
export function useMedia(consulta, porDefecto = false) {
  const leer = () => {
    try {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return porDefecto;
      }
      return window.matchMedia(consulta).matches;
    } catch {
      // Una consulta mal formada tira en algunos navegadores. Que el layout
      // degrade a su default es mejor que que la pantalla no monte.
      return porDefecto;
    }
  };

  const [coincide, setCoincide] = useState(leer);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    let mql;
    try {
      mql = window.matchMedia(consulta);
    } catch {
      return undefined;
    }
    // Se re-lee al suscribirse, no sólo al montar: entre el primer render y este
    // efecto el ancho pudo cambiar (una rotación durante la carga), y sin esto
    // el componente se quedaría con la foto vieja hasta el siguiente `change`.
    setCoincide(mql.matches);
    const alCambiar = (ev) => setCoincide(ev.matches);
    // `addEventListener` es lo moderno; `addListener` es el respaldo de Safari
    // viejo, que es exactamente el navegador de un iPhone que no se actualiza.
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', alCambiar);
      return () => mql.removeEventListener('change', alCambiar);
    }
    if (typeof mql.addListener === 'function') {
      mql.addListener(alCambiar);
      return () => mql.removeListener(alCambiar);
    }
    return undefined;
  }, [consulta, porDefecto]);

  return coincide;
}

/** El corte `md` de Tailwind, que es el que el Panel ya usa para bifurcar. */
export const CONSULTA_ANCHO = '(min-width: 768px)';

/* LAPIDA t7-3 remate 4 (C1): murio `CONSULTA_LG` (1024 de ventana), el corte fijo de la barra del avance del
   grupo; hoy esa barra decide por lo que cabe (`lib/disposicionDeLaBarra.js`). */

/** «¿Hay tan poco alto que una tarjeta de más taparía algo?» — el bloque de
 *  sesión de la barra lateral NO scrollea (`Sidebar.js:372` es hermano del
 *  `<nav>`, no hijo), así que lo que se meta ahí come alto fijo. */
export const CONSULTA_ALTO_CORTO = '(max-height: 699px)';

/** ¿Estamos en un ancho de escritorio? Por defecto SÍ: ver el default de arriba. */
export function useEsAncho() {
  return useMedia(CONSULTA_ANCHO, true);
}

/** ¿La ventana es demasiado baja para colgar nada más de la barra lateral? */
export function useAltoCorto() {
  return useMedia(CONSULTA_ALTO_CORTO, false);
}
